/**
 * Basket API service
 * Handles generic e-commerce basket operations for all product types
 */
import { apiClient, handleApiResponse } from './api';
import type {
  Basket,
  CreateBasketRequest,
  CreateBasketResult,
  AddBasketLinesRequest,
  CheckoutBasketRequest,
  CheckoutBasketResult,
  ApplyCreditRequest,
} from '@/types/basket';

// ==================== BASKET OPERATIONS ====================

/**
 * Create a new basket, optionally with initial line items
 */
export async function createBasket(request?: CreateBasketRequest): Promise<CreateBasketResult> {
  return handleApiResponse(apiClient.post('/baskets', request ?? {}));
}

/**
 * Get a basket by ID with all line items
 */
export async function getBasket(basketId: string): Promise<Basket> {
  return handleApiResponse(apiClient.get(`/baskets/${basketId}`));
}

/**
 * Get the authenticated member's active (Draft) basket for a source.
 * Returns null if the member has no open basket (204 No Content).
 */
export async function getActiveBasket(source: string): Promise<Basket | null> {
  const res = await apiClient.get(`/baskets/active?source=${encodeURIComponent(source)}`);
  if (res.status === 204) return null;
  return res.data?.data ?? res.data;
}

/**
 * Add one or more line items to a basket
 */
export async function addBasketLines(
  basketId: string,
  request: AddBasketLinesRequest
): Promise<Basket> {
  return handleApiResponse(apiClient.post(`/baskets/${basketId}/lines`, request));
}

/**
 * Remove a line item from a basket
 */
export async function removeBasketLine(
  basketId: string,
  lineId: string
): Promise<Basket> {
  return handleApiResponse(apiClient.delete(`/baskets/${basketId}/lines/${lineId}`));
}

/**
 * Checkout a basket - initiates payment via Stripe
 */
export async function checkoutBasket(
  basketId: string,
  request: CheckoutBasketRequest
): Promise<CheckoutBasketResult> {
  return handleApiResponse(apiClient.post(`/baskets/${basketId}/checkout`, request));
}

// ==================== CREDIT OPERATIONS ====================

/**
 * Apply or update the credit on a basket
 * Pass null creditId to remove any applied credit
 */
export async function applyCredit(
  basketId: string,
  request: ApplyCreditRequest
): Promise<Basket> {
  return handleApiResponse(apiClient.put(`/baskets/${basketId}/credit`, request));
}

/**
 * Cancel a basket and release any reserved credits
 */
export async function cancelBasket(basketId: string): Promise<Basket> {
  return handleApiResponse(apiClient.post(`/baskets/${basketId}/cancel`));
}

/**
 * Reset a basket back to Draft status after cancelled Stripe payment
 */
export async function resetBasketToDraft(basketId: string): Promise<Basket> {
  return handleApiResponse(apiClient.patch(`/baskets/${basketId}`, { status: 'Draft' }));
}
